import { APIClient } from "../helpers/api_helper";
import * as url from "../helpers/url_helper";
import { 
    WorkOrderItem, 
    WorkOrderCreatePayload,
    WorkOrderUpdatePayload
} from "../interfaces/WorkOrder";
import { WorkOrdersResponse } from "../slices/workOrders/types";
import { DEFAULT_PAGE_SIZE } from "../common/constants";
import axios from "axios";
import config from "../config";

const api = new APIClient();
const { api: apiConfig } = config;

// Helper function to get auth token from Redux Persist
const getAuthToken = () => {
  try {
    const persistedState = sessionStorage.getItem('persist:root');
    if (persistedState) {
      const parsed = JSON.parse(persistedState);
      if (parsed.Login) {
        const loginState = JSON.parse(parsed.Login);
        if (loginState.user && Object.keys(loginState.user).length > 0) {
          return loginState.user.token || null;
        }
      }
    }
  } catch (e) {
    console.error("Error reading auth token from Redux Persist:", e);
  }
  return null;
};

/**
 * Get all work orders with pagination
 * @param pageNumber - Page number (default: 1)
 * @param pageSize - Page size (default: DEFAULT_PAGE_SIZE, max: 200)
 * @returns Promise with paginated work orders response
 */
export const getWorkOrders = (
    pageNumber: number = 1,
    pageSize: number = DEFAULT_PAGE_SIZE
): Promise<WorkOrdersResponse> => {
    // Real API call with query parameters - typed response
    return api.get<WorkOrdersResponse>(url.GET_WORK_ORDERS, {
        pageNumber,
        pageSize,
    });
};

/**
 * Get work order by ID
 * @param id - Work order ID
 * @returns Promise with work order item
 */
export const getWorkOrderById = (id: string): Promise<WorkOrderItem> => {
    return api.get<WorkOrderItem>(`${url.GET_WORK_ORDER_BY_ID}/${id}`);
};

/**
 * Create a new work order
 * @param payload - Work order create payload
 * @returns Promise with created work order item
 */
export const createWorkOrder = (payload: WorkOrderCreatePayload): Promise<WorkOrderItem> => {
    // Only include optional fields if they have values
    const cleanPayload: any = {
        tenantId: payload.tenantId,
        clientId: payload.clientId,
        siteId: payload.siteId,
        workOrderNumber: payload.workOrderNumber,
        title: payload.title,
        startDate: payload.startDate,
        estimatedBudget: payload.estimatedBudget,
        chargeUpType: payload.chargeUpType,
        deposit: payload.deposit,
    };
    
    if (payload.description) cleanPayload.description = payload.description;
    if (payload.status !== undefined) cleanPayload.status = payload.status;
    if (payload.endDate) cleanPayload.endDate = payload.endDate;
    if (payload.completedDate) cleanPayload.completedDate = payload.completedDate;
    if (payload.statusColourCode) cleanPayload.statusColourCode = payload.statusColourCode;
    if (payload.notes) cleanPayload.notes = payload.notes;
    if (payload.projectId) cleanPayload.projectId = payload.projectId;
    if (payload.sourceQuoteId) cleanPayload.sourceQuoteId = payload.sourceQuoteId;
    if (payload.sourceQuoteJobId) cleanPayload.sourceQuoteJobId = payload.sourceQuoteJobId;
    
    return api.create<WorkOrderItem>(url.CREATE_WORK_ORDER, cleanPayload);
};

/**
 * Update an existing work order
 * @param id - Work order ID
 * @param payload - Work order update payload
 * @returns Promise with updated work order item
 */
export const updateWorkOrder = (id: string, payload: WorkOrderUpdatePayload): Promise<WorkOrderItem> => {
    return api.put<WorkOrderItem>(`${url.UPDATE_WORK_ORDER}/${id}`, payload);
};

/**
 * Delete a work order
 * @param id - Work order ID
 * @returns Promise that resolves when deletion is complete
 */
export const deleteWorkOrder = (id: string): Promise<void> => {
    return api.delete<void>(`${url.DELETE_WORK_ORDER}/${id}`);
};

/**
 * Generate a work order number
 * @param tenantId - Tenant ID
 * @returns Promise with generated work order number
 */
export const generateWorkOrderNumber = (tenantId: string): Promise<{ isSuccess: boolean; message: string; generatedCode: string }> => {
    return api.get<{ isSuccess: boolean; message: string; generatedCode: string }>(`${url.GENERATE_WORK_ORDER_NUMBER}/${tenantId}`);
};

export const autogenerateWorkOrderNumber = (): Promise<{ isSuccess: boolean; message: string; generatedCode: string }> => {
    console.log("workOrderService.autogenerateWorkOrderNumber called");
    return api.create<{ isSuccess: boolean; message: string; generatedCode: string }>(url.AUTOGENERATE_WORK_ORDER_NUMBER, {});
};

/**
 * Create a new work order with attachments
 * @param payload - Work order create payload with attachments
 * @param attachments - Array of File objects to attach
 * @returns Promise with created work order item
 */
export const createWorkOrderWithAttachments = (
    payload: WorkOrderCreatePayload,
    attachments: File[]
): Promise<WorkOrderItem> => {
    const token = getAuthToken();
    const formData = new FormData();

    // Append all work order fields
    formData.append("TenantId", payload.tenantId);
    formData.append("ClientId", payload.clientId);
    formData.append("SiteId", payload.siteId);
    formData.append("WorkOrderNumber", payload.workOrderNumber);
    formData.append("Title", payload.title);
    if (payload.description) formData.append("Description", payload.description);
    // Status is system-managed - only append if explicitly provided
    if (payload.status !== undefined) formData.append("Status", String(payload.status));
    formData.append("StartDate", payload.startDate);
    if (payload.endDate) formData.append("EndDate", payload.endDate);
    if (payload.completedDate) formData.append("CompletedDate", payload.completedDate);
    if (payload.statusColourCode) formData.append("StatusColourCode", payload.statusColourCode);
    formData.append("EstimatedBudget", String(payload.estimatedBudget));
    formData.append("ChargeUpType", String(payload.chargeUpType));
    formData.append("Deposit", String(payload.deposit));
    if (payload.notes) formData.append("Notes", payload.notes);
    // Optional fields - only append if they have values
    if (payload.projectId) formData.append("ProjectId", payload.projectId);
    if (payload.sourceQuoteId) formData.append("SourceQuoteId", payload.sourceQuoteId);
    if (payload.sourceQuoteJobId) formData.append("SourceQuoteJobId", payload.sourceQuoteJobId);

    // Append attachments (multiple files)
    attachments.forEach((file: File) => {
        formData.append("Attachments", file);
    });

    return axios.post<WorkOrderItem>(
        `${apiConfig.API_URL}${url.CREATE_WORK_ORDER_WITH_ATTACHMENTS}`,
        formData,
        {
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: "application/json",
                "Content-Type": "multipart/form-data",
            },
            timeout: 60000, // 60 seconds for file upload
        }
    ).then((response: any) => {
        const responseData = response.data || response;
        // Handle API response wrapper - response.result contains the work order
        if (responseData?.result) {
            return responseData.result as WorkOrderItem;
        }
        return responseData as WorkOrderItem;
    });
};

/**
 * Update an existing work order with attachments
 * @param id - Work order ID
 * @param payload - Work order update payload
 * @param attachments - Array of File objects to attach
 * @returns Promise with updated work order item
 */
export const updateWorkOrderWithAttachments = (
    id: string,
    payload: WorkOrderUpdatePayload,
    attachments: File[]
): Promise<WorkOrderItem> => {
    const token = getAuthToken();
    const formData = new FormData();

    // Append all work order fields
    formData.append("WorkOrderNumber", payload.workOrderNumber);
    formData.append("Title", payload.title);
    if (payload.description) formData.append("Description", payload.description);
    // Status is system-managed - only append if explicitly provided
    if (payload.status !== undefined) formData.append("Status", String(payload.status));
    formData.append("StartDate", payload.startDate);
    if (payload.endDate) formData.append("EndDate", payload.endDate);
    if (payload.completedDate) formData.append("CompletedDate", payload.completedDate);
    if (payload.statusColourCode) formData.append("StatusColourCode", payload.statusColourCode);
    formData.append("EstimatedBudget", String(payload.estimatedBudget));
    formData.append("ChargeUpType", String(payload.chargeUpType));
    formData.append("Deposit", String(payload.deposit));
    if (payload.notes) formData.append("Notes", payload.notes);

    // Append attachments (multiple files)
    attachments.forEach((file: File) => {
        formData.append("Attachments", file);
    });

    return axios.put<WorkOrderItem>(
        `${apiConfig.API_URL}${url.UPDATE_WORK_ORDER_WITH_ATTACHMENTS}/${id}/WithAttachments`,
        formData,
        {
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: "application/json",
                "Content-Type": "multipart/form-data",
            },
            timeout: 60000, // 60 seconds for file upload
        }
    ).then((response: any) => {
        const responseData = response.data || response;
        // Handle API response wrapper - response.result contains the work order
        if (responseData?.result) {
            return responseData.result as WorkOrderItem;
        }
        return responseData as WorkOrderItem;
    });
};

